import { createBrowserClient } from "@supabase/ssr";
import type { SupabaseClient } from "@supabase/supabase-js";

// Singleton — one GoTrueClient per browser context to prevent
// "Multiple GoTrueClient instances detected" warnings and the
// "Lock stolen by another request" auth lock conflicts.
//
// Stored on globalThis so HMR module re-evaluation doesn't recreate
// the client (each new client would race for the same auth lock).
const GLOBAL_KEY = "__gam_supabase_browser_client__";

interface GlobalWithClient {
  [GLOBAL_KEY]?: SupabaseClient;
}

// No-op lock implementation. With a single client instance in the page,
// the Web-Locks-based coordination isn't needed and causes spurious
// "Lock was released because another request stole it" AbortErrors when
// concurrent operations try to refresh the auth token.
async function noopLock<R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> {
  return fn();
}

/** Read a required public env var, failing with a name we can act on.
 *  Without this, a missing var surfaced as `undefined.trim()` inside a
 *  minified chunk — see the 2026-08-27 prerender break, where a fresh
 *  worktree had no .env.local and three dashboard pages died with a
 *  stack that named neither the variable nor this file. */
function requiredEnv(name: string, value: string | undefined): string {
  const v = value?.trim();
  if (!v) {
    throw new Error(
      `[supabase] Missing required env var ${name}. ` +
        `Set it in .env.local (local/worktree builds do NOT inherit it — ` +
        `.env.local is gitignored) or in the Vercel project env.`
    );
  }
  return v;
}

function buildClient(): SupabaseClient {
  return createBrowserClient(
    requiredEnv("NEXT_PUBLIC_SUPABASE_URL", process.env.NEXT_PUBLIC_SUPABASE_URL),
    // .trim() guards against a leading/trailing space in the env var, which
    // Supabase Realtime URL-encodes as '+' and causes WebSocket auth to fail.
    requiredEnv(
      "NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY",
      process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY
    ),
    {
      auth: {
        // Disable the navigator.locks-based coordination since we already
        // guarantee a single browser client via the singleton below.
        lock: noopLock,
      },
    }
  );
}

export function createClient(): SupabaseClient {
  if (typeof window === "undefined") {
    // Server-side: never cache; each request gets its own
    return buildClient();
  }
  const g = globalThis as GlobalWithClient;
  if (!g[GLOBAL_KEY]) {
    g[GLOBAL_KEY] = buildClient();
  }
  return g[GLOBAL_KEY]!;
}
